import { useQuery } from '@apollo/client';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Popper from '@mui/material/Popper';
import TextField from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { SearchLabels } from '../../pages/OilAndGasMap/Filters/searchTypes';
import { LEASE_COUNTRY_ISO_CODES } from '../../utils/constants';
import { getFulltextResultParameters } from '../../utils/globalSearch';
import { FilterLabels } from '../Filters/filterTypes';
import { getMatchedValueFromAutocomplete } from '../GlobalSearch/helper';
import { CustomOptions } from './CustomOptions';

// This matches the special characters in the BaseQuery search functionality found in
// app/queries/base_query.rb
const removeSpecialCharacters = str => str.replace(/[-_/\\()[\],"'.\s]/g, '');

// Custom search that ignores special characters in the options labels
const getFilterOptions = key =>
  createFilterOptions({
    stringify: option => removeSpecialCharacters(`${option[key]}`),
  });

const useStyles = makeStyles(theme => ({
  autoComplete: {
    margin: theme.spacing(1),

    '& .MuiInputBase-root': {
      paddingTop: '0px',
    },
  },
  chips: {
    height: '18px',
    minWidth: '1px',
  },
  chipsNoLimit: {
    width: 'auto',
  },
  chipsDelete: {
    height: '15px',
    margin: '0 0 0 -1px',
  },
  chipsLabel: {
    paddingLeft: '6px',
    paddingRight: '6px',
  },
  text: {
    fontSize: '14px',
    boxSizing: 'border-box',
    borderRadius: '5px',
  },
  inputBase: {
    marginLeft: '4px',
    padding: '10px 4px 3px 4px !important',
  },
  input: {
    fontFamily: theme.fonts.base,
    boxSizing: 'border-box',
    borderRadius: '5px',
  },
  inputNoLimit: {
    width: 'auto',
  },
  icon: {
    color: '#2a2e49',
  },
  listbox: {
    ...theme.palette.scrollbar,
    fontFamily: theme.fonts.base,
    fontSize: theme.fontSizes.base,
    maxHeight: '300px',
    overflowY: 'auto',
    margin: 0,
    padding: 0,
    whiteSpace: 'unset',
  },
  startAdornment: {
    display: 'flex',
    alignItems: 'center',
    maxWidth: '50%',
    position: 'relative',
    fontSize: '13px',
    marginTop: '10px',
  },
  startAdornmentNoLimit: {
    maxWidth: 'none',
    minWidth: '100px',
  },
  endAdornment: {
    display: 'flex',
    position: 'relative',
    top: 'auto',
  },
  fullHeight: {
    height: '100%',
  },
  asterisk: {
    color: '#e43246',
  },
  control: {
    padding: '5px 16px',
    width: '100%',
    height: '100%',
    boxSizing: 'border-box',
  },
  loaderWrapper: {
    paddingRight: '10px',
    display: 'flex',
    alignItems: 'center',
  },
  loadingMore: {
    display: 'flex',
    justifyContent: 'center',
    padding: '8px',
  },
}));

const useDynamicStyles = makeStyles(() => ({
  customOption: {
    marginBottom: props => (props.bottomOffset ? `${props.bottomOffset}px` : 0),
  },
}));

const CUSTOM_OPTION_TYPE = 'customOption';
const CUSTOM_OPTION = { key: CUSTOM_OPTION_TYPE, type: CUSTOM_OPTION_TYPE };

const CustomPopper = props => (
  <Popper {...props} style={{ minWidth: 290, maxWidth: 350 }} placement='bottom-start' />
);

const AutoCompleteCustomOptions = ({
  getData,
  onDataLoaded,
  menuItems,
  label,
  onChange,
  value,
  optionKey,
  groupOptionKey,
  propVariables = {},
  runQueryAfterOnChange,
  queryVariable,
  multiple,
  styles = {},
  menuItemsCallback,
  disabled,
  note,
  disableLimitTags,
  responseCallback,
  hideItem,
  control = {},
  context = '',
  required,
  globalSearchType,
  id,
  enablePagination = false,
  perPage = 50,
}) => {
  const classes = useStyles();

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const [groupedOptions, setGroupedOptions] = useState({});
  const [getValueFromSearchParams, setGetValueFromSearchParams] = useState(false);

  // Pagination states
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const listboxRef = useRef(null);

  const debouncedSearchValue = useDebounce(searchValue, 300);

  /* QUERY */
  // Append query variable to variables object for passing to query
  const variables = useMemo(() => {
    const vars = { ...propVariables };
    if (queryVariable) vars[queryVariable] = '';
    if (enablePagination) {
      vars.pagination = { page, perPage };
    }
    return vars;
  }, [propVariables, queryVariable, enablePagination, page, perPage]);

  const searchParams = getFulltextResultParameters();

  // Parse query response
  function parseQueryResponse(res) {
    // res structure is { <queryName>: <data> }, so we need to get the data
    // from the first key in the object
    // e.g. { "basinsWithFilter": [...] }
    // e.g. { "assetsByNameWithFilter": { assets: [], totalCount: 0 } }
    const value = Object.values(res)[0];
    if (!value) return { items: [], totalCount: 0 };
    const response = responseCallback ? responseCallback(value) : value;
    const items = response.filter(item => !hideItem || !hideItem(item));
    const totalCount = value.totalCount || items.length;
    return { items, totalCount };
  }

  const menuNameFilters = [
    FilterLabels.assets,
    SearchLabels.asset,
    SearchLabels.block,
    SearchLabels.facility,
    SearchLabels.field,
    SearchLabels.lngFacility,
    SearchLabels.well,
  ];

  const addMenuNameToOptions = (options, property) => {
    const properties = options.map(option => option[property]);
    const duplicates = properties.filter(
      (property, index) => index !== properties.lastIndexOf(property),
    );
    return options.map(option => {
      let menuName = option[property];
      if (duplicates.includes(option[property]) && option?.country?.displayName) {
        menuName += ` (${option.country.displayName})`;
      }
      return {
        ...option,
        menuName,
      };
    });
  };

  const postProcessOptions = options => {
    if (menuNameFilters.includes(label)) {
      if (options.every(option => option.displayName)) {
        options = addMenuNameToOptions(options, 'displayName');
      } else {
        options = addMenuNameToOptions(options, 'name');
      }
    }
    return options;
  };
  /** @TODO, fix.
   *
   **/
  // Query to populate the options
  const query = () =>
    // eslint-disable-next-line react-hooks/rules-of-hooks
    // biome-ignore lint/correctness/useHookAtTopLevel: Requires a bigger refactor(used in multiple places)
    useQuery(getData, {
      variables,
      fetchPolicy: enablePagination ? 'network-only' : 'cache-first',
      notifyOnNetworkStatusChange: true,
      onCompleted: res => {
        onDataLoaded?.(res);

        if (!res) return;

        if (enablePagination) {
          const { items, totalCount } = parseQueryResponse(res);

          if (page === 1) {
            const processedOptions = postProcessOptions(items);
            setOptions(processedOptions);
            setHasMore(processedOptions.length < totalCount);
            setIsLoadingMore(false);
          } else {
            setOptions(prev => {
              // Deduplicate based on id
              const newItems = items.filter(item => !prev.find(p => p.id === item.id));
              const processedNewItems = postProcessOptions(newItems);
              const updatedOptions = [...prev, ...processedNewItems];
              setHasMore(updatedOptions.length < totalCount);
              setIsLoadingMore(false);
              return updatedOptions;
            });
          }
        } else {
          const response = parseQueryResponse(res);
          const { items, groupedOptions } = menuItemsCallback
            ? menuItemsCallback(response.items || response)
            : {};
          const options = items || response.items || response;

          if (groupedOptions) {
            setGroupedOptions(groupedOptions);
          }
          setOptions(postProcessOptions(options));
        }

        if (globalSearchType && getValueFromSearchParams) {
          const searchedValue = options.find(option =>
            getMatchedValueFromAutocomplete(option, searchParams),
          );
          if (searchedValue) {
            onChange(searchedValue);
          }
          setGetValueFromSearchParams(false);
        }
      },
      onError: () => {
        if (enablePagination) {
          setIsLoadingMore(false);
        }
      },
      skip: disabled,
    });

  // Set variables from query or menuItems if static dataset
  const { loading, data, refetch } = getData
    ? query()
    : { loading: false, data: menuItems, refetch: null };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Requires a bigger refactor(used in multiple places)
  useEffect(() => {
    if (globalSearchType && searchParams && globalSearchType === searchParams.entity) {
      setGetValueFromSearchParams(true);
      // Leases use the block name in the dropdowns
      let name;
      if (globalSearchType === 'BLOCK' && LEASE_COUNTRY_ISO_CODES.includes(searchParams.isoCode)) {
        name = searchParams.name.split(' - ')[0];
      } else {
        ({ name } = searchParams);
      }
      setSearchValue(name);
    }
  }, [searchParams]);

  // Set options from menuItems if getData query doesn't exist
  // biome-ignore lint/correctness/useExhaustiveDependencies: Requires a bigger refactor(used in multiple places)
  useEffect(() => {
    if (!getData) setOptions(postProcessOptions(data));
  }, [menuItems]);

  // Refetch data on text change if runQueryAfterOnChange is true
  const textOnChange = event => setSearchValue(event.target.value);

  // Reset pagination when search changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: Requires a bigger refactor(used in multiple places)
  useEffect(() => {
    if (enablePagination && debouncedSearchValue !== '' && page !== 1) {
      setPage(1);
      setOptions([]);
      setHasMore(true);
      setIsLoadingMore(false);
    }
  }, [debouncedSearchValue, enablePagination]);

  // Debounce the text change
  // biome-ignore lint/correctness/useExhaustiveDependencies: Requires a bigger refactor(used in multiple places)
  useEffect(() => {
    if (runQueryAfterOnChange && refetch) {
      const refetchVariables = { ...propVariables, [queryVariable]: debouncedSearchValue };
      if (enablePagination) {
        refetchVariables.pagination = { page: 1, perPage };
      }
      refetch(refetchVariables);
    }
  }, [
    debouncedSearchValue,
    control.controlValues,
    runQueryAfterOnChange,
    enablePagination,
    perPage,
  ]);

  const renderCustomOptions = useMemo(() => !!note || !!control.controlItems, [note, control]);

  const [bottomOffset, setBottomOffset] = useState(0);
  const classesDynamic = useDynamicStyles({ bottomOffset });

  // Handle scroll to load more (for pagination)
  const handleScroll = useCallback(
    event => {
      if (!enablePagination || !hasMore || isLoadingMore || loading) return;

      const listboxNode = event.currentTarget;
      const position = listboxNode.scrollTop + listboxNode.clientHeight;
      const threshold = listboxNode.scrollHeight - 10;

      if (position >= threshold) {
        setIsLoadingMore(true);
        setPage(prev => prev + 1);
      }
    },
    [enablePagination, hasMore, isLoadingMore, loading],
  );

  const handleClose = useCallback(() => {
    setOpen(false);
    if (enablePagination) {
      setSearchValue('');
      setPage(1);
      setHasMore(true);
      setIsLoadingMore(false);
    }
  }, [enablePagination]);

  // Set options from filtering and make sure to append note and control if needed
  const filterOptions = (options, state) => {
    if (options?.length === 0) return [];

    // For pagination, don't filter locally when there's no search input
    // Let the server handle filtering
    if (enablePagination && !state.inputValue) {
      return [...options, ...(renderCustomOptions ? [CUSTOM_OPTION] : [])];
    }

    // Ignore special characters in the search
    state.inputValue = removeSpecialCharacters(state.inputValue);
    const results = getFilterOptions(optionKey)(options, state);
    // When items are grouped, we want to still be able to search for those items by name
    // Ex. CCUS Project is grouped by hub name
    // Hub name = Acorn CCS
    // Project name = Project Cavendish
    // If we search for Project Cavendish we want Acorn CCS to appear in the results
    if (results && groupOptionKey) {
      // Filter the results to get UNIQUE group keys
      const groupedResults = results
        ?.map(r => r[groupOptionKey])
        .filter((key, idx, self) => key && self.indexOf(key) === idx)
        .map(key => groupedOptions[key].parent);
      // Find which group keys are not already in the results
      const difference = groupedResults?.filter(
        gr =>
          !results?.find(
            r => gr[groupOptionKey] === r[groupOptionKey] && gr[optionKey] === r[optionKey],
          ),
      );

      // Add grouped items to the result
      results.push(...difference);
    }

    return [...results, ...(renderCustomOptions ? [CUSTOM_OPTION] : [])];
  };

  const isLoading = loading && (!enablePagination || page === 1);

  const renderCustomOption = useCallback(
    () => (
      <CustomOptions
        key={Math.random().toString(36).substring(7)}
        setBottomOffset={setBottomOffset}
        controlClass={classes.control}
        control={control}
        note={note}
        dataFromQuery={data}
        context={context}
      />
    ),
    [classes.control, control, context, data, note],
  );

  const renderRegularOption = useCallback(
    (props, option, state) => {
      // Check if this is the last option and we're loading more (for pagination)
      const isLastOption = enablePagination && state.index === options.length - 1;

      return (
        <React.Fragment key={option.id || option[optionKey]}>
          <li {...props}>{option[optionKey]}</li>
          {isLastOption && isLoadingMore && (
            <li className={classes.loadingMore}>
              <CircularProgress size={20} />
            </li>
          )}
        </React.Fragment>
      );
    },
    [classes.loadingMore, enablePagination, isLoadingMore, optionKey, options.length],
  );

  const renderOptionItem = useCallback(
    (props, option, state) => {
      if (option.type === CUSTOM_OPTION_TYPE) {
        return renderCustomOption();
      }
      return renderRegularOption(props, option, state);
    },
    [renderCustomOption, renderRegularOption],
  );

  return (
    <div id={id} style={{ display: 'block', height: disableLimitTags ? '100%' : 'auto' }}>
      <Autocomplete
        className={clsx({
          [classes.autoComplete]: true,
          [styles.root]: !!styles.root,
          [classes.fullHeight]: disableLimitTags,
        })}
        disableCloseOnSelect={true}
        disabled={disabled}
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        onChange={(e, val, reason, details = {}) => {
          e.preventDefault();
          e.stopPropagation();
          if (val?.type === CUSTOM_OPTION_TYPE) {
            return;
          }
          let returnValue = val;
          // check if option selected is part of a grouping
          const { option } = details;
          if (groupOptionKey && option) {
            const groupedOption = groupedOptions[option[groupOptionKey]];

            // If the option selected is the parent of a group, we need to add or remove all the child options
            if (groupedOption?.parent[optionKey] === option[optionKey]) {
              switch (reason) {
                // case in which option is selected, add it's grouped options
                case 'select-option':
                case 'selectOption':
                  returnValue = [...val, ...groupedOption.group];
                  break;
                // case in which option is removed, remove it's grouped options
                case 'remove-option':
                case 'removeOption':
                  returnValue = val.filter(v => !groupedOption.group.find(g => g.id === v.id));
                  break;
              }
            }
          }

          setOpen(false);
          onChange(returnValue, data);

          if (runQueryAfterOnChange && (returnValue === null || returnValue.length === 0)) {
            textOnChange({ target: { value: '' } });
          }
        }}
        value={value}
        multiple={multiple}
        limitTags={1}
        getOptionDisabled={option => option.isDisabled}
        isOptionEqualToValue={(option, value) => option[optionKey] === value[optionKey]}
        getOptionLabel={option => (option[optionKey] ? option[optionKey] : '')}
        options={[...options, ...(renderCustomOptions ? [CUSTOM_OPTION] : [])]}
        filterOptions={filterOptions}
        loading={isLoading}
        classes={{
          clearIndicatorDirty: classes.icon,
          popupIndicator: classes.icon,
          popperDisablePortal: classes.control,
          endAdornment: clsx({ [classes.endAdornment]: disableLimitTags }),
        }}
        slotProps={{
          chip: {
            classes: {
              root: clsx({ [classes.chips]: true, [classes.chipsNoLimit]: disableLimitTags }),
              deleteIcon: classes.chipsDelete,
              label: classes.chipsLabel,
              ...styles.chips,
            },
          },
          listbox: {
            className: clsx({
              [classes.listbox]: true,
              [classesDynamic.customOption]: renderCustomOptions,
              [styles.listbox]: !!styles.listbox,
            }),
            onScroll: enablePagination ? handleScroll : undefined,
            ref: enablePagination ? listboxRef : undefined,
          },
        }}
        renderOption={renderOptionItem}
        renderTags={(value, getTagProps) => {
          const visibleValues = hideItem ? value.filter(v => !hideItem(v)) : value;
          const numTags = visibleValues.length;
          const limitTags = disableLimitTags ? undefined : 1;

          return (
            <>
              {visibleValues.slice(0, limitTags).map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  // biome-ignore lint/suspicious/noArrayIndexKey: Ideally, ID should be used as index, does every case have an ID?
                  key={index}
                  label={option[optionKey]}
                  classes={{
                    root: clsx({ [classes.chips]: true, [classes.chipsNoLimit]: disableLimitTags }),
                    label: classes.chipsLabel,
                    deleteIcon: classes.chipsDelete,
                    ...styles.chips,
                  }}
                />
              ))}
              {numTags > limitTags && ` +${numTags - limitTags}`}
            </>
          );
        }}
        renderInput={params => (
          <TextField
            {...params}
            id={id}
            className={clsx({
              [classes.text]: true,
              [classes.fullHeight]: disableLimitTags,
              [styles.text]: styles.text,
            })}
            autoComplete='off'
            label={label.toUpperCase()}
            variant='filled'
            onChange={runQueryAfterOnChange ? textOnChange : () => {}}
            required={required}
            slotProps={{
              input: {
                ...params.InputProps,
                className: clsx({
                  [classes.input]: true,
                  [styles.input]: !!styles.input,
                  [classes.fullHeight]: disableLimitTags,
                }),
                classes: {
                  input: clsx(classes.inputBase, {
                    [classes.inputNoLimit]: disableLimitTags,
                  }),
                },
                disableUnderline: true,
                startAdornment: params.InputProps.startAdornment ? (
                  <div
                    className={clsx({
                      [classes.startAdornment]: true,
                      [classes.startAdornmentNoLimit]: disableLimitTags,
                    })}
                  >
                    {params.InputProps.startAdornment}
                  </div>
                ) : undefined,
                endAdornment: (
                  <React.Fragment>
                    {isLoading ? (
                      <div className={classes.loaderWrapper}>
                        <CircularProgress color='inherit' size={20} />
                      </div>
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              },

              htmlInput: {
                ...params.inputProps,
                autoComplete: 'new-password',
              },

              inputLabel: {
                ...params.InputLabelProps,
                classes: {
                  ...params.InputLabelProps?.classes,
                  asterisk: classes.asterisk,
                },
                className: styles.label || null,
              },
            }}
          />
        )}
        slots={{
          popper: CustomPopper,
        }}
      />
    </div>
  );
};

export default AutoCompleteCustomOptions;
